<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" :title="computedTitle" @show="onShow"
                   @hidden="onHidden" :content-in-form="false" :show-confirmation="showConfirmation"
                   :is-loading="loading" scrollable modal-class="mobile-full-screen">

      <b-form @submit.prevent="onSubmit">
        <div class="row mb-3">
          <div class="col-sm-6 col-xl-4">
            <template v-if="customer">
              <b-form-group>
                <label>{{ $t('Customer') }}</label>
                <b-input :value="`${customer.display_name} - ${customer.email}`" disabled/>
              </b-form-group>
            </template>
            <template v-else>
              <customer-select-input-widget
                :model="model" attribute="customer_uuid"
                :expand="'billing_address.country' + ($hasPermission('change_customerbankaccount') ? ',term' : '')"
                @select="onCustomerSelect" @unselect="onCustomerUnselect">
              </customer-select-input-widget>
            </template>
          </div>
          <div class="col-sm-6 col-xl-4">
            <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                          :max="$getMonthCurrent(model.created_at)"
                          :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
          </div>
          <div class="col-sm-6 col-xl-4" v-if="hasMoreThanOneLanguage">
            <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
          </div>
        </div>


        <div class="row">
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Customer Info') }}
                </div>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="customer || model.customer">
                  <customer-email-address-info :customer="customer ? customer : model.customer"
                                               :show-address="false"/>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Invoice Billing address') }}
                </div>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                          @click="onBillingAddressEditClick">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="model.invoice_billing_address">
                  <p v-html="$options.filters.asAddress(model.invoice_billing_address, false)" class="p-0"></p>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
        </div>


        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <b-button class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
              <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Add New Charge') }}
            </b-button>
          </div>
          <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
            <table class="info-table table table-sm miscellaneous-table">
              <tr>
                <th style="width: 25%">{{ $t('Description') }}</th>
                <th style="width: 25%">{{ $t('Comment') }}</th>
                <th style="width: 25%">{{ $t('Amount') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Taxable') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <tr :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
                <td class="d-flex pr-3">
                  <input-widget v-if="hasPredefinedMiscCharges" style="flex: 1" :items="invoiceMiscellaneousItemCharges"
                                value-field="key"
                                display-field="${item.value}"
                                @select2Select="onMiscChargeDescriptionDropdownValueChange(item)"
                                type="select2" :model="item" :label="false" attribute="title_dropdown_value"
                                :rules="item.amount ? 'required' : ''"/>
                  <input-widget v-if="item.title_dropdown_value === 'other' || !hasPredefinedMiscCharges"
                                style="flex: 1"
                                :class="{'ml-2': hasPredefinedMiscCharges}"
                                :rules="item.amount ? 'required|max:255' : 'max:255'"
                                :model="item" attribute="title" :label="false"/>
                </td>
                <td class="pr-3">
                  <input-widget value-field="key"
                                display-field="${item.value}"
                                type="text" :model="item" :label="false" attribute="comment"/>
                </td>
                <td class="pr-3">
                  <currency-input-widget :rules="item.title ? 'required' : ''" :model="item" :currency="currency"
                                         @input="calculateData" attribute="amount" :label="false"/>
                </td>
                <td class="text-center">
                  <input-widget @change="calculateData" attribute="taxable" :model="item" type="checkbox"
                                :label="false"/>
                </td>
                <td class="text-center">
                  <b-tooltip :target="`remove-commission-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor" :id="`remove-commission-button-${index}`"
                                     @click="onMiscellaneousItemDelete(index)" style="font-size: 1.5em;"
                                     v-if="model.miscellaneous_items.length > 1"
                                     :icon="'times'"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-5 offset-7">
            <invoice-totals-table :financial-items="[]" :model="model" hide-hammer hide-extra-charges hide-import-tax
                                  hide-premium hide-premium-vat hide-shipping :custom-labels="totalsLabels"
                                  :hide-misc-charge-vat="inclusiveTaxForFinancialObjects" :currency="currency"
                                  :skip-misc-charge-vat-from-total="inclusiveTaxForFinancialObjects"
                                  :miscellaneous-items="model.miscellaneous_items"/>
          </div>
        </div>
      </b-form>

      <template v-slot:modal-footer>
        <div class="d-flex align-items-center mr-auto text-muted" v-if="isRecalculating">
          <b-spinner class="mr-2"/>
          <h4 class="d-inline-block mb-0">
            {{ $t('Calculating') }}
          </h4>
        </div>
        <cancel-button @click="hideServiceInvoiceModal" :disabled="loading">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onSubmit)" :show-loader="loading"
                       :disabled="model.miscellaneous_items.length === 0">
          {{ $t('Submit') }}
        </submit-button>
      </template>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import CancelButton from "../../core/components/common/CancelButton";
  import SubmitButton from "../../core/components/common/SubmitButton";
  import InvoicesService from "./InvoicesService";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import {SOURCE_INVOICES_TABLE} from "../constants";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import MiscellaneousItem from "./models/MiscellaneousItem.model";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import _ from "lodash";
  import InvoiceTotalsTable from "@/modules/invoices/InvoiceTotalsTable";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import ServiceInvoiceModel from "@/modules/invoices/models/ServiceInvoice.model";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable.vue";
  import uuidV4 from 'uuid';
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: mapStateGlobal, mapActions: globalMapActions} = createNamespacedHelpers('global');

  export default {
    name: "ServiceInvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      NoDataAvailable,
      LanguageCodeSelectInputWidget,
      InvoiceTotalsTable,
      CustomerSelectInputWidget,
      CustomerEmailAddressInfo,
      CurrencyInputWidget,
      SubmitButton,
      CancelButton,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        modalUuid: uuidV4(),
        customerUuid: null,
        loading: false,
        showConfirmation: true,
        model: new ServiceInvoiceModel(),
        isRecalculating: false,
        totalsLabels: {
          miscItem: this.$i18n.t('Amount'),
          miscItemVat: this.$i18n.t('VAT')
        }
      }
    },
    computed: {
      ...mapStateGlobal([
        'hasMoreThanOneLanguage',
        'invoiceComplaintStatuses',
        'invoiceMiscellaneousItemCharges',
        'inclusiveTaxForFinancialObjects',
        'currentBranch',
        'hasStripeProvider'
      ]),
      ...mapState({
        modal: state => state.serviceInvoiceModal,
        customer: state => state.serviceInvoiceModal.customer,
        editObject: state => state.serviceInvoiceModal.object,
      }),
      computedTitle() {
        if (this.modal.creditNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Invoice {no}', {no: this.model.invoice_no})
        }
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Generate Service Invoice')
        }
        return this.$i18n.t('Update Invoice {no}', {no: this.model.invoice_no})
      },
      hasPredefinedMiscCharges() {
        return this.invoiceMiscellaneousItemCharges && this.invoiceMiscellaneousItemCharges.length > 1;
      },
      currency() {
        if (this.model && this.model.currency) {
          return this.model.currency.symbol
        }
        return null
      }
    },
    watch: {
      async customer() {
        this.$nextTick(function () {
          if (this.customer) {
            this.model.customer_uuid = this.customer.uuid;
            this.setDefaultAddresses(this.customer.billing_address, this.customer.shipping_address)
            this.customerUuid = this.customer.uuid
          }
        })
      },
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new ServiceInvoiceModel(this.editObject);
          let cfc = this.currentBranch.tax_rate / 100 + 1

          this.model.miscellaneous_items = this.model.miscellaneous_items.map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = Math.round(parseFloat(item.amount * cfc))
            }
            return new MiscellaneousItem(item)
          });

          if (this.editObject.customer) {
            this.customerUuid = this.editObject.customer.uuid

            if (!this.model.preferred_language_code) {
              this.model.preferred_language_code = this.editObject.customer.preferred_language_code
            }
          }

          this.setDefaultAddresses(
            this.editObject.invoice_billing_address,
            null,
          )
        }
      },
    },
    methods: {
      ...mapActions(['hideServiceInvoiceModal', 'getTableData']),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        if (!this.model.uuid) {
          this.model.miscellaneous_items.push(new MiscellaneousItem({taxable: true}))
        }
        this.showConfirmation = true;
        this.loading = false;
      },
      onHidden() {
        this.model = new ServiceInvoiceModel();
        this.customerUuid = null;
        this.hideServiceInvoiceModal();
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }
        this.model.resetErrors();

        this.loading = true;
        let response;

        let unpaidInvoiceNotificationSuccess = false;
        if (!this.model.uuid || this.modal.creditNoteWithoutRefundParams) {
          unpaidInvoiceNotificationSuccess = await this.$confirm(
            "Would you like to send `New Invoice Notification` to the customer?"
          );
        }

        let cfc = this.currentBranch.tax_rate / 100 + 1

        const requestData = {
          ...this.model.toJSON(),
          invoice_billing_address: this.model.invoice_billing_address,
          type: 'service',
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            if (this.modal.creditNoteWithoutRefundParams) {
              delete item.uuid;
            }
            return item
          }),
          complaint_status: this.model.complaint_status || null,
          send_unpaid_invoice_notification: unpaidInvoiceNotificationSuccess,
          comment: this.model.comment,
        };

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          requestData['exclude_from_auto_payments'] = this.model.exclude_from_auto_payments;
        }

        if (this.modal.creditNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.credit_note_without_refund_params = this.modal.creditNoteWithoutRefundParams;
          response = await InvoicesService.createInvoice(requestData);
        } else if (!this.model.uuid) {
          response = await InvoicesService.createInvoice(requestData);
        } else {
          response = await InvoicesService.updateInvoice(requestData.uuid, requestData);
        }
        this.loading = false;
        if (response.success) {
          this.showConfirmation = false;

          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getTableData();
          }

          if (!this.model.uuid && this.$hasPermission('export_invoice')) {
            const invoice = response.body.invoice;
            this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
              title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no}),
            });
            let pdfDataResponse = await InvoicesService.getInvoicePdfData(invoice.uuid);
            if (pdfDataResponse.success) {
              this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
                ...pdfDataResponse.body,
                filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
              });
            } else {
              let msg = this.$i18n.t("An error occurred during PDF generation");
              if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
                msg = pdfDataResponse.body.non_field_errors
              }
              this.$errorDialog(msg);
              this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
            }
          }
          this.hideServiceInvoiceModal();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.body.miscellaneous_items) {
            for (let index in response.body.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(response.body.miscellaneous_items[index]);
            }
          } else if (response.body && typeof response.body === 'object') {
            this.model.setMultipleErrors(response.body);
          } else {
            this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
          }
        }
      },
      calculateData: _.debounce(async function () {
        const isFormValid = await this.$refs.observer.validate();
        if (!isFormValid) {
          return;
        }
        this.isRecalculating = true;

        let cfc = this.currentBranch.tax_rate / 100 + 1

        let {success, body} = await InvoicesService.calculateData({
          uuid: this.model.uuid,
          invoice_billing_address: this.model.invoice_billing_address,
          customer_uuid: this.model.customer ? this.model.customer.uuid : null,
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            return item
          }),
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          type: 'service',
        }, 'miscellaneous_items');

        if (success) {
          if (body.miscellaneous_items.length > 0) {
            for (let miscItem of body.miscellaneous_items) {
              let itemIndex;
              if (miscItem.uuid) {
                itemIndex = this.model.miscellaneous_items.findIndex(mi => mi.uuid === miscItem.uuid);
              } else {
                itemIndex = this.model.miscellaneous_items.findIndex(mi =>
                  (mi.title === miscItem.title || mi.title_dropdown_value === miscItem.title) && mi.amount == miscItem.amount
                );
              }

              if (itemIndex >= 0) {

                if (miscItem.taxable && this.inclusiveTaxForFinancialObjects) {
                  miscItem.amount = Math.round(parseFloat(miscItem.amount * cfc))
                }

                if (miscItem.uuid) {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    ...miscItem
                  })
                } else {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    amount: miscItem.amount,
                    vat: miscItem.vat,
                    taxable: miscItem.taxable,
                    title: miscItem.title,
                  }, true);
                }
              }
            }
            this.model.miscellaneous_items = [...this.model.miscellaneous_items];
          }
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(body);
          }
        }

        this.isRecalculating = false;
      }, 500),
      onCustomerSelect(customer) {
        this.model.customer = customer;
        this.customerUuid = customer.uuid
        this.model.preferred_language_code = customer.preferred_language_code;
        this.setDefaultAddresses(customer.billing_address)
        if (!this.model.uuid && hasPermission('change_customerbankaccount') && customer.term && this.hasStripeProvider) {
          this.model.exclude_from_auto_payments = customer.term.exclude_from_auto_payments;
        }
        this.$nextTick(() => {
          this.calculateData();
        })
      },
      onCustomerUnselect() {
        this.$nextTick(() => {
          this.calculateData();
        })
      },
      onTaxRateInputChange(value) {
        this.model.resetErrors();
        this.calculateData(value);
      },
      onMiscChargeDescriptionDropdownValueChange(item) {
        item.title = '';
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .items-table, .miscellaneous-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }
  }

  .miscellaneous-table {
    .misc-item-row {
      td:nth-child(3), td:nth-child(4) {
        vertical-align: middle;
      }
    }
  }
</style>
